import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Archive, LogOut, MonitorDot, Settings } from 'lucide-react';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useAuthStore } from '@shared/stores/authStore';
import { effectiveGuardAuthMode } from '@shared/lib/guardAuth';
import { usePanicAlarm } from '@features/noc/usePanicAlarm';
import { env } from '@config/env';

export function NocLayout(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const branding = useBrandingStore((s) => s.branding);
  const authMethod = useAuthStore((s) => s.authMethod);
  const user = useAuthStore((s) => s.user);

  usePanicAlarm();

  const title = branding?.name ?? env.appName;
  const subtitle =
    authMethod === 'api_key'
      ? 'API key · kiosk'
      : user?.email
        ? user.email
        : 'Staff session';

  const onHistory = location.pathname.startsWith('/history');

  return (
    <div className="flex min-h-screen flex-col bg-white text-ink">
      {/* ── Brand header ─────────────────────────────────────────── */}
      <header className="safe-top flex shrink-0 items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          {branding?.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt=""
              className="h-9 w-9 shrink-0 rounded object-contain"
            />
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold tracking-tight">{title}</p>
            <p className="truncate text-xs text-neutral-500">Guard NOC · {subtitle}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {effectiveGuardAuthMode() !== 'user' && (
            <button
              type="button"
              aria-label="Provision"
              className="rounded p-2 text-neutral-600 hover:bg-neutral-100"
              onClick={() => navigate('/provision')}
            >
              <Settings className="h-5 w-5" />
            </button>
          )}
          <button
            type="button"
            aria-label="Sign out"
            className="rounded p-2 text-neutral-600 hover:bg-neutral-100"
            onClick={async () => {
              await useAuthStore.getState().signOut();
              navigate(effectiveGuardAuthMode() === 'user' ? '/login' : '/provision', { replace: true });
            }}
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-auto pb-20">
        <Outlet />
      </main>

      {/* ── Bottom tabs ──────────────────────────────────────────── */}
      <nav className="pb-safe fixed inset-x-0 bottom-0 z-30 grid grid-cols-2 border-t border-neutral-200 bg-white">
        <TabButton
          label="Monitor"
          icon={<MonitorDot className="h-5 w-5" strokeWidth={2} />}
          active={!onHistory}
          onClick={() => navigate('/')}
        />
        <TabButton
          label="History"
          icon={<Archive className="h-5 w-5" strokeWidth={2} />}
          active={onHistory}
          onClick={() => navigate('/history')}
        />
      </nav>
    </div>
  );
}

interface TabButtonProps {
  label: string;
  icon: JSX.Element;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, icon, active, onClick }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${
        active ? 'text-red-600' : 'text-neutral-500 hover:text-neutral-700'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
